import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { useDispatch, useSelector } from 'react-redux'
import { Typography } from '@mui/material'
import { ILinkSubcontractorContext, LinkSubcontractorContext } from './LinkSubcontractor.context'
import { TabsSelector } from 'src/components/shared/tabs-selector/TabsSelector'
import { LinkSubcontractorTable } from './link-subcontractor-table/LinkSubcontractorTable'
import { ManageCustomers } from 'src/components/manage-customers/ManageCustomers'
import { AcceptCustomerInvite, FetchMCCustomers } from 'src/constants/actionTypes'
import { getNotDeactivatedMCCustomers } from 'src/selectors/mc'
import { MCStatus } from 'src/constants/mc/enums'
import { colors } from 'src/theme'
import { Button } from 'src/components/shared/Button'
import { CircleRightMajor } from '@shopify/polaris-icons'
import AcceptInviteImage from 'src/assets/images/accept-invite.svg'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { MCCustomer } from 'src/interfaces/mc'
import { EmptySubcontractors } from './EmptySubcontractors'
import { ReloadWrapper } from '../wrapper/reload-wrapper/ReloadWrapper'
import { showToast } from 'src/utils/toast'

const LinkSubcontractorComponent = () => {
  const dispatch = useDispatch()

  const mcCustomers = useSelector(getNotDeactivatedMCCustomers)

  const [selectedMCCustomerId, setSelectedMCCustomerId] = useState<string>(
    mcCustomers[0]?.customer.id || '',
  )
  const [isManageCustomersOpen, setIsManageCustomersOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState<string>('')

  const selectedMCCustomer = useMemo(
    () => mcCustomers.find((item) => item.customer.id === selectedMCCustomerId),
    [mcCustomers, selectedMCCustomerId],
  )

  const openManageCustomers = useCallback(() => {
    setIsManageCustomersOpen(true)
  }, [])

  const closeManageCustomers = useCallback(() => {
    setIsManageCustomersOpen(false)
  }, [])

  useEffect(() => {
    const fetchMCCustomers = async () => {
      try {
        const { data }: { data: MCCustomer[] } = await createAsyncAction(
          dispatch,
          FetchMCCustomers.request(),
        )

        const filteredData = data.filter(
          (item) => item.status !== MCStatus.Deactivated && item.status !== MCStatus.Rejected,
        )

        setSelectedMCCustomerId(filteredData[0]?.customer.id || '')
      } catch (error) {
        showToast('Something went wrong while fetching customers', { variant: 'error' })
      }
    }

    fetchMCCustomers()
  }, [])

  const handleAcceptInvite = useCallback(async () => {
    try {
      await createAsyncAction(
        dispatch,
        AcceptCustomerInvite.request({ customerId: selectedMCCustomerId }),
      )

      await createAsyncAction(dispatch, FetchMCCustomers.request())

      showToast('Invite accepted successfully', { variant: 'success' })
    } catch (error) {
      showToast('Something went wrong while accepting the invite', { variant: 'error' })
    }
  }, [selectedMCCustomerId])

  const linkSubcontractorContextValue = useMemo<ILinkSubcontractorContext>(() => {
    return {
      selectedMCCustomerId,

      searchQuery,
      setSearchQuery,
    }
  }, [selectedMCCustomerId, searchQuery, setSearchQuery])

  if (mcCustomers && !mcCustomers.length) {
    return <EmptySubcontractors />
  }

  return (
    <LinkSubcontractorContext.Provider value={linkSubcontractorContextValue}>
      <div>
        <div css={tw`flex flex-row items-center justify-between mb-6 gap-4`}>
          <Typography variant="h2" css={tw`mb-2`}>
            Link to Customer
          </Typography>
          <TabsSelector
            menuLabel="Select customer"
            selectedValue={selectedMCCustomerId}
            onSelect={setSelectedMCCustomerId}
            options={mcCustomers.map((item) => ({
              label: item.customer.displayName,
              value: item.customer.id,
            }))}
          />
          <div>
            <ManageCustomers
              isManageCustomersOpen={isManageCustomersOpen}
              openManageCustomers={openManageCustomers}
              closeManageCustomers={closeManageCustomers}
            />
          </div>
        </div>
        <ReloadWrapper>
          <LinkSubcontractorTable
            overlay={
              selectedMCCustomer?.status === MCStatus.Invited ? (
                <div
                  css={tw`absolute inset-0 bg-[rgba(0,0,0,0.35)] rounded-lg z-[999] flex items-center justify-center`}
                >
                  <div css={tw`bg-white rounded-2xl p-4 pt-6 w-[370px]`}>
                    <img src={AcceptInviteImage} css={tw`ml-[50%] translate-x-[-50%]`} />
                    <Typography
                      variant="h4"
                      css={tw`text-center my-2`}
                    >{`You've been invited by ${selectedMCCustomer.customer.displayName}`}</Typography>
                    <Typography
                      variant="body2"
                      color={colors.SHADES_GREY_400}
                      css={tw`text-center mb-4`}
                    >
                      You're almost in — just sign the contract to accept the invite and start
                      linking contractors to the customer.
                    </Typography>
                    <Button
                      rightIcon={<CircleRightMajor width={20} fill={colors.PRIMARY_PURPLE_500} />}
                      className={tw`w-full`}
                      size="small"
                      color="light"
                      onClick={handleAcceptInvite}
                    >
                      Accept invite
                    </Button>
                  </div>
                </div>
              ) : undefined
            }
          />
        </ReloadWrapper>
      </div>
    </LinkSubcontractorContext.Provider>
  )
}

export const LinkSubcontractor = memo(LinkSubcontractorComponent)
